import { SET_SERIES, ADD_POINT_UNITS } from './actiontypes';
import { queryTimeSeriesV2 } from '../../api/Api';
import { roundFloatStr, displayDuration } from '../../utils/parsing';

const addMultipleSeriesAction = (series) => ({
  type: SET_SERIES,
  series,
});

const DAY_MS = 24 * 60 * 60 * 1000;
const MINS_IN_DAY = 24 * 60;

const RESAMPLE_THRESHOLDS = [
  [7 * DAY_MS, 1], // 7 days or less can sample at 1m
  [30 * DAY_MS, 5], // up to 30 days can sample at 5m
  [90 * DAY_MS, 60], // hourly from 30 to 90
  // every interval higher will be daily
];

const getResampleThreshold = (start, end) => {
  const interval = end - start;
  for (let i = 0; i < RESAMPLE_THRESHOLDS.length; i += 1) {
    const [threshold, sampleMins] = RESAMPLE_THRESHOLDS[i];
    if (interval < threshold) return sampleMins;
  }
  return MINS_IN_DAY;
};

const fetchTimeSeriesActionCreator = (pointIds, start, end) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const resampleMinutes = getResampleThreshold(start, end);
  if (resampleMinutes > 1) {
    const { val, unit } = displayDuration(resampleMinutes);
    // eslint-disable-next-line no-console
    console.log(`Query automatically resampled to ${val} ${unit} (${resampleMinutes}m)`);
  }
  // See resources.query.TimeseriesQuery for the schema
  return (
    queryTimeSeriesV2(jwt, { point_ids: pointIds, start, end }, resampleMinutes)
      .then((points) => {
        const units = points.reduce((m, p) => {
          // eslint-disable-next-line no-param-reassign
          m[p.point_id] = p.unit;
          return m;
        }, {});
        dispatch({ type: ADD_POINT_UNITS, data: units });

        const series = points.map((r) => {
          const timeIndex = r.columns.indexOf('time');
          const valueIndex = r.columns.indexOf(r.unit);
          return {
            pointId: r.point_id,
            raw: r.raw,
            unit: r.unit,
            values: r.values.map((v) => ({
              timestamp: new Date(v[timeIndex]).getTime(),
              value: roundFloatStr(v[valueIndex]),
            })),
          };
        });
        dispatch(addMultipleSeriesAction(series));
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(`Error fetching points: ${e}`))
  );
};

const FETCH_DELAY = 50; // ms
const POINTS_PER_FETCH = 20;
// wait up to {FETCH_DELAY} before sending fetches, to allow multiple trend graphs to
// all fetch with a single API call
let queuedFetch = null;

const TIMESTAMP_MARGIN = 5000; // 5 seconds
const withinMargin = (a, b) => Math.abs(a - b) <= TIMESTAMP_MARGIN;

export const queuedTimeSeriesActionCreator = (pointIds, start, end) => (dispatch) => {
  if (queuedFetch == null) {
    queuedFetch = {
      timeout: setTimeout(() => {
        queuedFetch = null;
        dispatch(fetchTimeSeriesActionCreator(pointIds, start, end));
      }, FETCH_DELAY),
      pointIds,
      start,
      end,
    };
  } else if (withinMargin(start, queuedFetch.start) && withinMargin(end, queuedFetch.end)) {
    clearTimeout(queuedFetch.timeout);
    const newPoints = Array.from(new Set(queuedFetch.pointIds.concat(pointIds)));
    const fetch = () => {
      queuedFetch = null;
      dispatch(fetchTimeSeriesActionCreator(newPoints, start, end));
    };
    if (newPoints.length >= POINTS_PER_FETCH) {
      fetch();
    } else {
      queuedFetch.pointIds = newPoints;
      queuedFetch.timeout = setTimeout(fetch, FETCH_DELAY);
    }
  } else {
    // not within the margin (asking for a different time range) so don't batch this request
    dispatch(fetchTimeSeriesActionCreator(pointIds, start, end));
  }
};
